import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpErrorResponse } from '@angular/common/http';
import {
  catchError,
  switchMap,
  throwError,
  BehaviorSubject,
  filter,
  take,
} from 'rxjs';
import { AuthService } from '@app/services/auth/auth.service';
import { Router } from '@angular/router';
import { environment } from '@env/environment';
import { app_main_routing_menu } from '@app/shared/app-main-routing-menu';

const UNAUTHORIZED_ERROR_STATUS = 401;

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Skip authentication when using mock data
  if (request.url.includes(environment.apiMockUrl)) {
    return next(request);
  }

  // Skip authentication for auth-related endpoints
  if (
    request.url.includes('oauth') ||
    request.url.includes('token') ||
    request.url.includes('.well-known') ||
    request.url.includes('auth/token') ||
    request.url.includes('auth/refresh-token') ||
    request.url.includes('ssobeta.stirista.com') ||
    request.url.includes('app-version')
  ) {
    return next(request);
  }

  // Get access token and add to request
  const token = authService.getAccessToken();

  if (!token) {
    // No token available - let request proceed
    // The backend will return 401 if authentication is required
    return next(request);
  }

  // Add token to request
  const authRequest = request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`,
      // Temporary add x user token from the client side until we have an API gateway solution for this
      'X-User-Token': token,
    },
  });

  return next(authRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === UNAUTHORIZED_ERROR_STATUS) {
        if (isRefreshing) {
          // If refresh is already in progress, wait for it to complete
          return refreshTokenSubject.pipe(
            filter(token => token !== null),
            take(1),
            switchMap(newToken => {
              // Retry the request with new token
              const retryRequest = request.clone({
                setHeaders: {
                  Authorization: `Bearer ${newToken}`,
                  // Temporary add x user token from the client side until we have an API gateway solution for this
                  'X-User-Token': newToken,
                },
              });
              return next(retryRequest);
            }),
          );
        } else {
          // Start refresh process
          isRefreshing = true;
          refreshTokenSubject.next(null);

          return authService.refreshToken().pipe(
            switchMap(newToken => {
              isRefreshing = false;
              if (newToken) {
                // Emit the new token to waiting requests
                refreshTokenSubject.next(newToken);
                // Retry the request with new token
                const retryRequest = request.clone({
                  setHeaders: {
                    Authorization: `Bearer ${newToken}`,
                    'X-User-Token': newToken,
                  },
                });
                return next(retryRequest);
              }
              // Refresh failed, logout will handle redirect
              refreshTokenSubject.next(null);
              authService.logout();
              return throwError(() => error);
            }),
            catchError((refreshError: HttpErrorResponse) => {
              isRefreshing = false;
              refreshTokenSubject.next(null);
              // Request failed even after refresh token, logout will handle redirect
              if (refreshError.status === UNAUTHORIZED_ERROR_STATUS) {
                authService.logout();
              }
              return throwError(() => refreshError);
            }),
          );
        }
      }

      if (error.status === 403) {
        // TODO: Check with the team if should redirect or display a friendly message to the user
        // Forbidden - user doesn't have permission
        router.navigate([app_main_routing_menu.forbidden.path]);
        return throwError(() => error);
      }

      // For all other errors, re-throw them
      return throwError(() => error);
    }),
  );
};
